import React, { useState } from 'react';
import { FolderOpen, Loader2, Zap } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import { DARK, btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from './theme';
import DestinationStep from './DestinationStep';
import IngestProgressPanel from './progress/IngestProgressPanel';

export default function ScanAndOffload({
  c, device, jobId, scanning, scanResult, scanError, sourcePath, chooseSourceFolder,
  destPath, chooseDestination,
  templates, selectedTemplateId, setSelectedTemplateId, projectRoot, chooseProjectRoot,
  selectedTemplate, templateVariables, setTemplateVariable, resolvedPath, resolving,
  createTemplate, deleteTemplate, effectiveDestPath, missingVariables,
  offloading, progress, progressPct, startIngest, cancelIngest,
  remoteConnections, selectedConnectionIds, toggleConnectionId,
  matchedProfile, startFromAutomation, dismissMatchedProfile,
}) {
  const isDarkMode = c === DARK;
  const profileForThisDevice = matchedProfile?.drive?.device === device.device ? matchedProfile.profile : null;
  const [operation, setOperation] = useState('copy');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>
      <div style={{ fontSize: '13px', fontWeight: 600 }}>{device.description || device.label}</div>

      {profileForThisDevice && scanResult && !offloading && !progress && (
        <div style={{ padding: '12px 14px', border: `1px solid ${c.accent}`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Zap size={16} color={c.accent} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: '12px' }}>
            <div style={{ fontWeight: 600 }}>Matches automation "{profileForThisDevice.name}"</div>
            <div style={{ color: c.muted, marginTop: '2px' }}>
              → {profileForThisDevice.projectName}{profileForThisDevice.scriptFileName ? ` · "${profileForThisDevice.scriptFileName}"` : ''}{(profileForThisDevice.remoteConnectionIds?.length > 0) ? ` · uploads to ${profileForThisDevice.remoteConnectionIds.length} remote server${profileForThisDevice.remoteConnectionIds.length === 1 ? '' : 's'}` : ''}
            </div>
          </div>
          <button onClick={() => startFromAutomation(profileForThisDevice)} style={btnStyle(c, 'primary')}>Start</button>
          <button onClick={dismissMatchedProfile} style={btnStyle(c, 'ghost')}>Ignore</button>
        </div>
      )}

      {!offloading && !progress && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontSize: '11px', color: c.muted, fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{sourcePath}</div>
          {/* Narrowing to a subfolder is a local-mount concept -- a remote
              source (device.mountPath === null) always pulls its whole
              configured root, so this control doesn't apply there. */}
          {device.mountPath !== null && (
            <button onClick={chooseSourceFolder} style={btnStyle(c, 'ghost')}>
              <FolderOpen size={13} /> {sourcePath === device.mountPath ? 'Narrow to folder' : 'Change folder'}
            </button>
          )}
        </div>
      )}

      {scanning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: c.muted }}>
          <Loader2 size={14} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> Scanning…
        </div>
      )}
      {scanError && <div style={{ fontSize: '12px', color: c.err }}>{scanError}</div>}

      {scanResult && !offloading && !progress && (
        <div style={{ padding: '12px 14px', border: `1px solid ${c.rule}`, borderRadius: '8px', background: c.subtleBg, fontSize: '12px' }}>
          <div>Camera profile: <strong>{scanResult.cameraProfile.family}</strong></div>
          <div>{scanResult.fileCount} files · {(scanResult.totalBytes / 1e9).toFixed(2)} GB</div>
          {scanResult.cameraProfile.family === 'Unknown' && sourcePath === device.mountPath && (
            <div style={{ marginTop: '8px', color: c.warn, fontSize: '11px' }}>
              No recognized camera format found — this might not be a dedicated card (could be a general-purpose hard disk).
              If it has unrelated folders, use &quot;Narrow to folder&quot; above to scope the ingest instead of copying everything.
            </div>
          )}
        </div>
      )}

      {scanResult && !offloading && !progress && (
        <DestinationStep
          c={c} destPath={destPath} chooseDestination={chooseDestination}
          templates={templates} selectedTemplateId={selectedTemplateId} setSelectedTemplateId={setSelectedTemplateId}
          projectRoot={projectRoot} chooseProjectRoot={chooseProjectRoot}
          selectedTemplate={selectedTemplate} templateVariables={templateVariables} setTemplateVariable={setTemplateVariable}
          resolvedPath={resolvedPath} resolving={resolving}
          createTemplate={createTemplate} deleteTemplate={deleteTemplate}
        />
      )}

      {scanResult && effectiveDestPath && missingVariables.length > 0 && !offloading && !progress && (
        <div style={{ fontSize: '11px', color: c.warn }}>
          Fill in {missingVariables.map(v => `{${v}}`).join(', ')} above before starting — an empty field becomes a folder literally named "untitled".
        </div>
      )}

      {scanResult && effectiveDestPath && missingVariables.length === 0 && !offloading && !progress && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            Operation
            <PillDropdown
              value={operation}
              onChange={setOperation}
              isDarkMode={isDarkMode}
              options={[
                { value: 'copy', label: 'Verified copy' },
                { value: 'copy_and_proxy', label: 'Verified copy + proxy' },
                { value: 'proxy_only', label: 'Proxy only (no copy)' },
              ]}
            />
          </div>
          {remoteConnections.length > 0 && (
            <div>
              <div style={{ fontSize: '11px', color: c.muted, marginBottom: '6px' }}>
                Also upload to (any number at once):
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {remoteConnections.map(conn => (
                  <label key={conn.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedConnectionIds.includes(conn.id)}
                      onChange={() => toggleConnectionId(conn.id)}
                    />
                    {conn.name} <span style={{ color: c.muted }}>({conn.protocol}://{conn.host})</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => startIngest({ operation })} style={btnStyle(c, 'primary')}>
            Start Ingest
          </button>
        </>
      )}

      {(offloading || progress) && (
        <IngestProgressPanel c={c} jobId={jobId} progress={progress} progressPct={progressPct} cancelIngest={cancelIngest} />
      )}
    </div>
  );
}
